import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  User, 
  Lock, 
  Bell, 
  Globe, 
  Save,
  Eye,
  EyeOff,
  Copy,
  Check
} from 'lucide-react';

export function SettingsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="border-b border-slate-700">
        <nav className="flex gap-4">
          <TabButton 
            label="Profile" 
            icon={User} 
            active={activeTab === 'profile'} 
            onClick={() => setActiveTab('profile')} 
          />
          <TabButton 
            label="Security" 
            icon={Lock} 
            active={activeTab === 'security'} 
            onClick={() => setActiveTab('security')} 
          />
          <TabButton 
            label="Notifications" 
            icon={Bell} 
            active={activeTab === 'notifications'} 
            onClick={() => setActiveTab('notifications')} 
          />
          <TabButton 
            label="System" 
            icon={Globe} 
            active={activeTab === 'system'} 
            onClick={() => setActiveTab('system')} 
          />
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'profile' && <ProfileSettings user={user} />}
      {activeTab === 'security' && <SecuritySettings />}
      {activeTab === 'notifications' && <NotificationSettings />}
      {activeTab === 'system' && <SystemSettings />}
    </div>
  );
}

interface TabButtonProps {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  active: boolean;
  onClick: () => void;
}

function TabButton({ label, icon: Icon, active, onClick }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
        active 
          ? 'border-blue-500 text-blue-400' 
          : 'border-transparent text-slate-400 hover:text-white'
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

function ProfileSettings({ user }: { user: any }) {
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
  });
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    // API call would go here
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="card max-w-2xl">
      <h3 className="text-lg font-semibold text-white mb-6">Profile Information</h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">Name</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="input w-full"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">Email</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="input w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">Role</label>
          <input
            type="text"
            value="Administrator"
            disabled
            className="input w-full bg-slate-700 cursor-not-allowed"
          />
        </div>

        <div className="pt-4">
          <button onClick={handleSave} className="btn btn-primary flex items-center gap-2">
            {saved ? <Check className="h-5 w-5" /> : <Save className="h-5 w-5" />}
            {saved ? 'Saved!' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SecuritySettings() {
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    if (form.newPassword !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (form.newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setError('');
    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    }, 2000);
  };

  return (
    <div className="card max-w-2xl">
      <h3 className="text-lg font-semibold text-white mb-6">Change Password</h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">Current Password</label>
          <div className="relative">
            <input
              type={showCurrent ? 'text' : 'password'}
              value={form.currentPassword}
              onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
              className="input w-full pr-10"
            />
            <button
              type="button"
              onClick={() => setShowCurrent(!showCurrent)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
            >
              {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">New Password</label>
          <div className="relative">
            <input
              type={showNew ? 'text' : 'password'}
              value={form.newPassword}
              onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
              className="input w-full pr-10"
            />
            <button
              type="button"
              onClick={() => setShowNew(!showNew)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
            >
              {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">Confirm New Password</label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              value={form.confirmPassword}
              onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
              className="input w-full pr-10"
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
            >
              {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {error && (
          <p className="text-red-400 text-sm">{error}</p>
        )}

        <div className="pt-4">
          <button onClick={handleSave} className="btn btn-primary flex items-center gap-2">
            {saved ? <Check className="h-5 w-5" /> : <Save className="h-5 w-5" />}
            {saved ? 'Password Updated!' : 'Update Password'}
          </button>
        </div>
      </div>

      <div className="mt-8 pt-6 border-t border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-4">Two-Factor Authentication</h3>
        <p className="text-slate-400 text-sm mb-4">
          Add an extra layer of security to your account by enabling two-factor authentication.
        </p>
        <button className="btn btn-secondary">
          Enable 2FA
        </button>
      </div>
    </div>
  );
}

function NotificationSettings() {
  const [settings, setSettings] = useState({
    emailAlerts: true,
    failedJobs: true,
    weeklyReport: true,
    newSubscriptions: false,
  });

  const handleToggle = (key: keyof typeof settings) => {
    setSettings({ ...settings, [key]: !settings[key] });
  };

  return (
    <div className="card max-w-2xl">
      <h3 className="text-lg font-semibold text-white mb-6">Notification Preferences</h3>
      
      <div className="space-y-4">
        <ToggleSetting
          label="Email Alerts"
          description="Receive email notifications for important events"
          checked={settings.emailAlerts}
          onChange={() => handleToggle('emailAlerts')}
        />
        <ToggleSetting
          label="Failed Job Notifications"
          description="Get notified when push notification jobs fail"
          checked={settings.failedJobs}
          onChange={() => handleToggle('failedJobs')}
        />
        <ToggleSetting
          label="Weekly Reports"
          description="Receive weekly summary of push notification statistics"
          checked={settings.weeklyReport}
          onChange={() => handleToggle('weeklyReport')}
        />
        <ToggleSetting
          label="New Subscription Alerts"
          description="Get notified when new subscribers are added"
          checked={settings.newSubscriptions}
          onChange={() => handleToggle('newSubscriptions')}
        />
      </div>
    </div>
  );
}

interface ToggleSettingProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: () => void;
}

function ToggleSetting({ label, description, checked, onChange }: ToggleSettingProps) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-700 last:border-0">
      <div>
        <p className="text-white font-medium">{label}</p>
        <p className="text-sm text-slate-400">{description}</p>
      </div>
      <button
        onClick={onChange}
        className={`relative w-12 h-6 rounded-full transition-colors ${
          checked ? 'bg-blue-500' : 'bg-slate-600'
        }`}
      >
        <span
          className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : ''
          }`}
        />
      </button>
    </div>
  );
}

function SystemSettings() {
  const vapidKey = 'BJc-nnWNCSijN4-4Oc9TnWfXoyVfYVCARnIQUUpUH1svdf-IGPHCCs7IT6PiT3k4APyfB-PFxvDQTQipT266rwU';
  const [copied, setCopied] = useState(false);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="card max-w-2xl">
        <h3 className="text-lg font-semibold text-white mb-6">VAPID Configuration</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Public VAPID Key</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={vapidKey}
                readOnly
                className="input flex-1 font-mono text-xs"
              />
              <button 
                onClick={() => handleCopy(vapidKey)}
                className="btn btn-secondary px-3"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Use this key in your client-side JavaScript for push subscription
            </p>
          </div>
        </div>
      </div>

      <div className="card max-w-2xl">
        <h3 className="text-lg font-semibold text-white mb-6">API Configuration</h3>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">API Endpoint</label>
              <input
                type="text"
                value="https://push.techril.com/api/v1"
                readOnly
                className="input w-full font-mono text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">WebSocket Endpoint</label>
              <input
                type="text"
                value="wss://push.techril.com/ws"
                readOnly
                className="input w-full font-mono text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card max-w-2xl">
        <h3 className="text-lg font-semibold text-white mb-6">System Information</h3>
        
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-slate-400">Server Version</p>
            <p className="text-white font-medium">1.0.0</p>
          </div>
          <div>
            <p className="text-slate-400">Node.js Version</p>
            <p className="text-white font-medium">v20.x</p>
          </div>
          <div>
            <p className="text-slate-400">Database</p>
            <p className="text-white font-medium">PostgreSQL 16</p>
          </div>
          <div>
            <p className="text-slate-400">Queue System</p>
            <p className="text-white font-medium">BullMQ + Redis</p>
          </div>
        </div>
      </div>

      <div className="card max-w-2xl border-red-500/30 border">
        <h3 className="text-lg font-semibold text-red-400 mb-4">Danger Zone</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-slate-700">
            <div>
              <p className="text-white font-medium">Clear All Notifications</p>
              <p className="text-sm text-slate-400">Remove all notification history from the database</p>
            </div>
            <button className="btn btn-danger">
              Clear History
            </button>
          </div>
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="text-white font-medium">Regenerate VAPID Keys</p>
              <p className="text-sm text-slate-400">Warning: This will invalidate all existing subscriptions</p>
            </div>
            <button className="btn btn-danger">
              Regenerate
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
